<script setup lang="ts">
/**
 * Zeigt den Harmonika-Griff einer angeklickten Note der Griffschrift-**Ausgabe**:
 * den aktuell verwendeten Griff (rot auf dem Instrument) + alle spielbaren Alternativen
 * (Hover → gelbe Vorschau), gerankt nach Ergonomie. Ruft `POST /api/note-grips`.
 *
 * Konsumiert von PreviewPane (Tab „Griffschrift") mit der Ausgabe-MusicXML
 * (`previewFileContent`) + der ausgewählten Note der jeweiligen `MusicXmlPreview`.
 * Wirkt nur auf die AUSGABE (die Note kodiert ihren Griff selbst); auf der Eingabe nicht verwendet.
 */
import {computed, shallowRef, watch} from 'vue';
import {Icon} from '@iconify/vue';

import type {NoteInfo} from '@/composables/musicXmlDom';
import {
  applyGripToNoteInMusicXml,
  extractMeasureAsStandaloneMusicXml,
  highlightNoteInStandaloneMeasure,
} from '@/composables/musicXmlDom';
import OsmdMeasure from '@/components/measure-selection/OsmdMeasure.vue';

import InstrumentBody from './InstrumentBody.vue';
import {gripButtonIds, type Grip} from './gripTypes';
import type {BellowsDirection} from './harmonika.types';
import {gripButtonNoteMap} from './noteGrips';
import {formatNoteWithOctaveForDisplay} from './noteParse';
import {useNoteGrips} from '@/composables/useNoteGrips';
import {sameMembers} from '@/shared/sameMembers';
import type {IOSMDOptions} from "osmd-types-player";

// musicXml ist ein reiner INPUT (der Inspector schreibt ihn nie; Griff-Änderungen reisen als
// `applyGrip`-Emit) – deshalb Prop, kein defineModel (die Eltern binden ohnehin one-way).
const {selectedNote = null, musicXml = undefined} = defineProps<{
  selectedNote?: NoteInfo | null;
  musicXml?: string | null;
}>();

// Der Nutzer wählt eine Alternative → Parent tauscht den Griff der Ausgabe-Note (client-seitig).
const emit = defineEmits<{
  applyGrip: [grip: Grip];
}>();

// Fetch-Logik (debounced + Abbruch) liegt im Composable, geteilt mit dem Griff-Overlay.
const {response, loading, error} = useNoteGrips(() => musicXml ?? undefined, () => selectedNote);

const hovered = shallowRef<Grip | null>(null);

// Hover-Vorschau zurücksetzen, sobald sich Datei/Auswahl ändert (frische Griffdaten kommen).
watch([() => musicXml, () => selectedNote], () => {
  hovered.value = null;
});

function sameMap(a: Map<string, string>, b: Map<string, string>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) if (b.get(key) !== value) return false;
  return true;
}

const assignedGrip = computed(() => response.value?.assigned ?? null);

// Objekt-Rückgaben (Set/Map) mit oldValue-Pattern referenz-stabil halten (webapp/src/VUE_RULES.md).
const activeIds = computed((old?: Set<string>) => {
  const next = gripButtonIds(assignedGrip.value);
  return sameMembers(old, next) ? old : next;
});

const previewIds = computed((old?: Set<string>) => {
  const next = gripButtonIds(hovered.value);
  return sameMembers(old, next) ? old : next;
});

const labelMap = computed((old?: Map<string, string>) => {
  const next = gripButtonNoteMap(assignedGrip.value, hovered.value);
  return old && sameMap(old, next) ? old : next;
});

function gripLabel(rowIdx: number, btnIdx: number): string {
  const note = labelMap.value.get(`${rowIdx}-${btnIdx}`);
  return note ? formatNoteWithOctaveForDisplay(note) : '';
}

// Ein angeklickter Knopf klingt in der Balgrichtung des gezeigten Griffs (Hover-Vorschau zuerst).
const shownDirection = computed<BellowsDirection>(() => (
    (hovered.value ?? assignedGrip.value)?.is_druck === false ? 'ZUG' : 'DRUCK'
));

function directionLabel(isDruck: boolean): string {
  return isDruck ? 'Druck' : 'Zug';
}

function isAssignedGrip(grip: Grip): boolean {
  return sameMembers(gripButtonIds(grip), activeIds.value);
}

function applyAlternative(grip: Grip): void {
  if (isAssignedGrip(grip)) return; // aktueller Griff → nichts zu tun
  emit('applyGrip', grip);
}

// ── Notations-Vorschau (Griffschrift-Ausgabe je Griff) ─────────────────────────
// Die inspizierte Note wird amber markiert (nur in der Vorschau, nie im echten Output/Export), damit
// der Unterschied zwischen den sonst fast gleichen Takt-Thumbnails der Alternativen auffällt.
const PREVIEW_HIGHLIGHT_COLOR = '#d97706'; // amber-600

/** Eigenständiges Einzeltakt-MusicXML (Zieltakt) mit amber markierter inspizierter Note. */
function buildPreviewFragment(scoreXml: string, note: NoteInfo): string | null {
  const fragment = extractMeasureAsStandaloneMusicXml(
      scoreXml, note.staffIndex, note.measureNumber, note.measureIndex,
  );
  if (!fragment) return null;
  return highlightNoteInStandaloneMeasure(fragment, note.voice, note.voiceNoteIndex, PREVIEW_HIGHLIGHT_COLOR);
}

// Aktuelle Note: der Output zeigt den zugewiesenen Griff bereits → Takt direkt aus dem
// unveränderten `musicXml` ausschneiden (kein applyGrip nötig). String → keine oldValue-Stabilität nötig.
const assignedFragment = computed<string | null>(() => {
  const note = selectedNote;
  if (!note || note.isRest || !musicXml || !response.value?.assigned) return null;
  return buildPreviewFragment(musicXml, note);
});

// Alternativen: Griff via kanonischem `applyGripToNoteInMusicXml` anwenden (Vorschau == „Anwenden"),
// dann den Zieltakt ausschneiden. Array-Rückgabe referenz-stabil halten (webapp/src/VUE_RULES.md).
const alternativeFragments = computed<(string | null)[]>((old) => {
  const note = selectedNote;
  const xml = musicXml;
  const alts = response.value?.alternatives ?? [];
  if (!note || note.isRest || !xml || !alts.length) return old && old.length === 0 ? old : [];
  const next = alts.map((alt) => {
    const applied = applyGripToNoteInMusicXml(xml, note, alt.buttons, alt.is_druck, alt.bass_lyric);
    return applied ? buildPreviewFragment(applied, note) : null;
  });
  return old && old.length === next.length && old.every((v, i) => v === next[i]) ? old : next;
});

const hint = computed(() => {
  if (!musicXml) return 'Noch keine Griffschrift erzeugt.';
  if (!selectedNote) return 'Klicke eine Note in der Griffschrift, um ihren Griff zu sehen.';
  if (selectedNote.isRest) return 'Pause ausgewählt – kein Griff.';
  return '';
});
const zoom = 0.2
const options: IOSMDOptions = {cursorsOptions: [], autoResize: false, disableCursor: true, backend: 'canvas', drawingParameters: 'compacttight'}
const osmdClass = 'scale-80  items-center inline-flex justify-center'
const width = '400px'
const height = 'auto'
</script>

<template>

  <section class="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white/70 p-3 md:p-4">
  <header class="flex flex-wrap items-center gap-2">
    <Icon icon="fa6-solid:hand-point-up" class="text-gray-400"/>
    <h3 class="text-sm font-semibold text-gray-800">Griff</h3>
    <template v-if="response">
        <span class="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
          {{ response.sounding_notes.join(', ') || '—' }}
        </span>
      <span
          v-if="response.assigned"
          class="rounded px-2 py-0.5 text-xs font-medium"
          :class="response.assigned.is_druck ? 'bg-gray-800 text-white' : 'bg-blue-600 text-white'"
      >
          {{ directionLabel(response.assigned.is_druck) }}
        </span>
      <span v-if="response.is_chord" class="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          Akkord
        </span>
    </template>
    <Icon v-if="loading" icon="fa6-solid:spinner" class="animate-spin text-gray-400"/>
  </header>

  <p v-if="hint" class="py-4 text-center text-sm text-gray-500">{{ hint }}</p>

  <p v-else-if="error" class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{{ error }}</p>

  <template v-else-if="response">
    <p v-if="!response.assigned" class="text-sm text-gray-500">
      Für diese Note ist kein Griff hinterlegt (nicht spielbar).
    </p>

    <InstrumentBody
        :active-buttons="activeIds"
        :preview-buttons="previewIds"
        :get-label="gripLabel"
        :direction="shownDirection"
    />

    <div v-if="assignedFragment" class="flex flex-col gap-1">
      <p class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Aktuell</p>
      <div class="overflow-hidden rounded-md border border-amber-200 bg-white">
        <OsmdMeasure :height="height" :width="width" :class="osmdClass" :zoom="zoom" :options="options" :music-xml="assignedFragment"/>
      </div>
    </div>

    <div v-if="response.alternatives.length" class="flex flex-col gap-1">
      <p class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
        Alternativen ({{ response.alternatives.length }}) – zum Übernehmen anklicken
      </p>
      <ul class="flex flex-col gap-1">
        <li
            v-for="(alt, idx) in response.alternatives"
            :key="idx"
            class="flex flex-col gap-1  border-t-2 border-gray-600 px-2 py-1.5 text-sm transition-colors"
            :class="isAssignedGrip(alt)
                  ? 'cursor-default'
                  : 'cursor-pointer hover:border-amber-300 hover:bg-amber-50'"
            @mouseenter="hovered = alt"
            @mouseleave="hovered = null"
            @click="applyAlternative(alt)"
        >
          <div class="flex items-center gap-2">
              <span
                  class="w-12 shrink-0 rounded px-1.5 py-0.5 text-center text-xs font-medium"
                  :class="alt.is_druck ? 'bg-gray-800 text-white' : 'bg-blue-600 text-white'"
              >
                {{ directionLabel(alt.is_druck) }}
              </span>
            <span class="font-medium text-gray-800">
                {{ alt.buttons.map((b) => b.note).join(' + ') || '—' }}
              </span>
            <span class="text-xs text-gray-400">
                {{ alt.buttons.map((b) => `R${b.row}·${b.number}`).join('  ') }}
              </span>
            <span
                v-if="isAssignedGrip(alt)"
                class="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700"
            >
                aktuell
              </span>
            <span class="ml-auto flex items-center gap-2">
                <span class="font-mono text-xs text-gray-400" title="Ergonomie-Score (kleiner = besser)">
                  {{ alt.score.toFixed(1) }}
                </span>
                <span
                    v-if="!isAssignedGrip(alt)"
                    class="btn btn-primary "
                >
                  <Icon icon="fa6-solid:check" class="text-[10px]"/>
                  Anwenden
                </span>
              </span>
          </div>

          <div v-if="alternativeFragments[idx]" class="overflow-hidden rounded-md border border-gray-300/30 group-hover:border-sky-500/80 bg-white">
            <OsmdMeasure :height="height" :width="width" :class="osmdClass" :zoom="zoom" :options="options" :music-xml="alternativeFragments[idx]!"/>
          </div>
        </li>
      </ul>
    </div>
  </template>
  </section>
</template>
